import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

/**
 * Plattform-Erkennung für die optionale Android-App (Capacitor-Hülle).
 *
 * Die Hülle lädt die Live-Seite (Bauart A, docs/android-app-plan.md 1.2) —
 * derselbe Code läuft also im Browser UND in der App. Jede App-Weiche fragt
 * NUR diese Funktionen, nie `Capacitor` direkt: so bleibt die Unterscheidung
 * an einer Stelle, und im Browser ändert sich nichts (alles hier liefert dort
 * `false` bzw. `null`).
 *
 * Keine Funktion wirft. Eine kaputte oder fehlende Brücke zählt als Browser —
 * lieber der gewohnte Web-Weg als ein Absturz beim Start.
 */

/** Läuft die Seite in der nativen App-Hülle (Android)? */
export function istNativeApp(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function userAgent(): string {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string' ? navigator.userAgent : '';
  } catch {
    return '';
  }
}

/**
 * Variante der App (Vertrag E11/E12): Handy = Begehungs-Umfang, Tablet =
 * voller Desktop-Umfang. Beide laden dieselbe Live-Seite; die Hülle hängt je
 * Variante `SiGeKoApp/handy` bzw. `SiGeKoApp/tablet` an den User-Agent
 * (Capacitor `appendUserAgent`).
 */
export type AppVariante = 'handy' | 'tablet';

const VARIANTE_IM_UA = /\bSiGeKoApp\/(handy|tablet)\b/i;

/**
 * Synchron — schon beim ersten Render bekannt, ohne Brücken-Aufruf.
 * Browser → `null`. App ohne Zusatz (APK vor Einführung der Varianten) →
 * `'handy'`: das war bis dahin die einzige Variante.
 */
export function appVariante(): AppVariante | null {
  if (!istNativeApp()) return null;
  const treffer = VARIANTE_IM_UA.exec(userAgent());
  return treffer && treffer[1].toLowerCase() === 'tablet' ? 'tablet' : 'handy';
}

/** Android-Gerät im normalen Browser (nicht in der App) — Zielgruppe des Download-Hinweises. */
export function istAndroidBrowser(): boolean {
  return /Android/i.test(userAgent()) && !istNativeApp();
}

/**
 * iPhone/iPad. iPadOS meldet sich seit Version 13 wie ein Desktop-Mac
 * („Macintosh"); verraten wird es nur durch den Touchscreen.
 */
export function istIosGeraet(): boolean {
  const ua = userAgent();
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  let touchPunkte = 0;
  try {
    touchPunkte = typeof navigator !== 'undefined' ? Number(navigator.maxTouchPoints) || 0 : 0;
  } catch {
    touchPunkte = 0;
  }
  return /Macintosh/i.test(ua) && touchPunkte > 1;
}

/** Ist das native Plugin in dieser App-Version vorhanden (ältere APKs kennen neue Plugins nicht)? */
export function hatPlugin(name: string): boolean {
  try {
    return Capacitor.isPluginAvailable(name);
  } catch {
    return false;
  }
}

/**
 * Version der installierten App (Android: versionName/versionCode).
 * Im Browser `null`. Ebenfalls `null`, wenn die Abfrage scheitert oder der
 * versionCode keine ganze Zahl ist — die Update-Prüfung soll bei einer
 * unbekannten Version lieber schweigen als falsch „Pflicht-Update" melden.
 */
export async function nativeBuild(): Promise<{ versionName: string; versionCode: number } | null> {
  if (!istNativeApp()) return null;
  try {
    const info = await App.getInfo();
    const roh = String(info.build ?? '').trim();
    if (!/^\d+$/.test(roh)) return null;
    return { versionName: String(info.version ?? ''), versionCode: Number(roh) };
  } catch (e) {
    console.warn('[nativePlatform] App-Version nicht lesbar:', e);
    return null;
  }
}
